import React, { Fragment } from "react";
import {
  PlxsText,
  PlxsIcon,
  PlxsButton,
  PlxsCallout,
} from "@plexlogic/plexus-ui";
import { formatNumber } from "../../utils/formatUtils";
import { toSentenceCase } from "../../utils/stringUtils";
import { extractHttpErrorMessage } from "../../utils/errorUtils";
import { semanticColors } from "../../theme/styles";
import { apiService } from "../../services/api";
import useUploadStore, { 
  useCurrentDrawId,
  getRowCount,
  getHeaderRowColumns,
  getEntryRow1Columns,
  getEntryRow2Columns 
} from "./useUploadStore";

interface FilePreviewProps {
  onRemove: () => void;
  onRetry?: () => void;
  onDownload?: () => void;
  error?: string;
  isProcessing?: boolean;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  onRemove,
  onRetry,
  onDownload,
  error,
  isProcessing,
}) => {
  // Get all data from upload store
  const currentDrawId = useCurrentDrawId();
  const entriesUpload = useUploadStore((state: any) => state.entriesUpload);
  
  // Extract file information from upload store
  const fileName = entriesUpload?.originalFileName || 'Unknown';
  const rowCount = entriesUpload ? getRowCount(entriesUpload) : 0;

  // Extract CSV preview data from upload store
  const headerRowColumns = getHeaderRowColumns(entriesUpload);
  const entryRow1Columns = getEntryRow1Columns(entriesUpload);
  const entryRow2Columns = getEntryRow2Columns(entriesUpload);
  
  // Build firstFewRows from CSV preview data
  const firstFewRows = [
    headerRowColumns,
    entryRow1Columns,
    entryRow2Columns,
  ].filter(row => row.length > 0);

  const storeError = useUploadStore((state: any) => state.error);
  const uploadStatus = useUploadStore((state: any) => state.status);

  const handleDownload = async () => {
    if (onDownload) {
      onDownload();
    } else {
      // Use actual API to download file
      try {
        const storedFileName = entriesUpload?.storedFileName;

        if (!currentDrawId || !storedFileName) {
          alert("Unable to download: missing draw ID or file information");
          return;
        }

        const blob = await apiService.downloadEntryFile(
          currentDrawId,
          storedFileName
        );

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = entriesUpload?.originalFileName || fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        const errorMessage = extractHttpErrorMessage(error, "Download failed");
        alert(`Download failed: ${errorMessage}`);
      }
    }
  };

  return (
    <Fragment>
      <div
        style={{
          border: `1px solid var(--plexus-border-subtle, ${semanticColors.borderLight})`,
          borderRadius: 8,
          padding: 16,
          backgroundColor: `var(--plexus-surface-subtle, ${semanticColors.backgroundSubtle})`,
        }}
      >
        {/* File Header */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginBottom: 16,
          }}
        >
          {/* Top row: File info and buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ flex: 1, minWidth: "200px" }}>
              {/* File Icon and Name */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <PlxsIcon name="file" />
                <div style={{ wordBreak: "break-word" }}>
                  <PlxsText>{fileName}</PlxsText>
                </div>
              </div>

              {/* Processing Indicator */}
              {isProcessing && (
                <div style={{ marginBottom: 8 }}>
                  <PlxsText size="sm">Processing file...</PlxsText>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              {/* Download Button - only show if file is successfully processed */}
              {!error && !storeError && !isProcessing && rowCount && (
                <PlxsButton
                  isIconOnly
                  label="Download"
                  tone="brand"
                  variant="secondary"
                  size="sm"
                  dataTestId="download-file-button"
                  functionalIcon="download"
                  onClick={handleDownload}
                />
              )}
              {(error || storeError) && onRetry && (
                <PlxsButton
                  isIconOnly
                  functionalIcon="sync"
                  label="Retry"
                  onClick={onRetry}
                  variant="primary"
                  size="sm"
                  tone="brand"
                  dataTestId="retry-upload-button"
                />
              )}
              <PlxsButton
                isIconOnly
                functionalIcon="bin"
                tone="urgent"
                label="Remove"
                onClick={onRemove}
                variant="secondary"
                size="sm"
                dataTestId="remove-file-button"
              />
            </div>
          </div>
        </div>

        {/* All Error Messages - Centralized Error Display */}
        {(storeError || error) && (
          <div style={{ marginBottom: 12 }}>
            <PlxsCallout 
              tone="urgent"
              dataTestId="file-errors"
              message={
                <div>
                  <div style={{ marginBottom: 8 }}>
                    <PlxsText size="sm" weight="semiBold">
                      {uploadStatus === "error" ? "Upload Failed" : "File Error"}
                    </PlxsText>
                  </div>
                  <div>
                    {/* Display store/upload errors with priority - these come from API responses */}
                    {storeError && (
                      <div style={{ marginBottom: 4 }}>
                        <PlxsText size="sm">{storeError}</PlxsText>
                      </div>
                    )}
                    
                    {/* Display component prop errors only if no store error */}
                    {error && !storeError && (
                      <div style={{ marginBottom: 4 }}>
                        <PlxsText size="sm">{error}</PlxsText>
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* File Preview Table */}
        {firstFewRows && firstFewRows.length > 0 && !error && !storeError && !isProcessing && (
          <div style={{ marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <PlxsText size="sm">
                {rowCount > 0
                  ? `Preview (${firstFewRows.length} of ${formatNumber(
                      rowCount
                    )} rows):`
                  : `Preview (first ${firstFewRows.length} rows):`}
              </PlxsText>
            </div>
            <div
              style={{
                border: `1px solid var(--plexus-border-subtle, ${semanticColors.borderLight})`,
                borderRadius: 6,
                overflow: "hidden",
                backgroundColor: `var(--plexus-surface-base, ${semanticColors.surface})`,
                width: "100%",
              }}
            >
              <div
                style={{
                  overflow: "auto",
                  maxHeight: 300,
                  width: "100%",
                }}
              >
                <table
                  style={{
                    width: "max-content",
                    minWidth: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <tbody>
                    {firstFewRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            style={{
                              padding: "6px 8px",
                              borderBottom:
                                rowIndex < firstFewRows.length - 1
                                  ? `1px solid var(--plexus-border-subtle, ${semanticColors.borderLight})`
                                  : "none",
                              borderRight:
                                cellIndex < row.length - 1
                                  ? `1px solid var(--plexus-border-subtle, ${semanticColors.borderLight})`
                                  : "none",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              minWidth: "80px",
                              maxWidth: "150px",
                              backgroundColor:
                                rowIndex === 0
                                  ? `var(--plexus-surface-subtle, ${semanticColors.backgroundSubtle})`
                                  : "transparent",
                              fontWeight: rowIndex === 0 ? 500 : "normal",
                            }}
                            title={cell || "(empty)"}
                          >
                            {cell ? toSentenceCase(cell) : "(empty)"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {firstFewRows[0] && firstFewRows[0].length > 10 && (
              <div
                style={{
                  marginTop: 8,
                  color: `var(--plexus-text-muted, ${semanticColors.textMuted})`,
                }}
              >
                <PlxsText size="xs">
                  Showing {firstFewRows[0].length} columns. Scroll horizontally
                  to see all data.
                </PlxsText>
              </div>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default FilePreview;
